/* OsCatalogDetailsViewRenderer.js */

"use strict";
const _ = require("lodash");
const Forms = require("@gluenetworks/forms");
const FormsComponent = Forms.ui.component;
const OsCatalogSkusComponent = require("./oscatalogskus/OsCatalogSkusComponent");
const OsCatalogImagesComponent = require("./oscatalogimages/OsCatalogImagesComponent");
class OsCatalogDetailsViewRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("os-catalog-details-view-gl");
        this._rootElem.component = this.model;
        this._logger = this.model.logger;
        this._isDirty = false;
        this._formsComponent = new FormsComponent();
        this._pendingPatches = [];

        this.model.gameEditor.addViewAction({
            type: "normal",
            displayName: "Save",
            name: "SaveOsCatalogDetails",
            handler: (this._onOsCatalogDetailsSave = this._onOsCatalogDetailsSave.bind(this))
        });
        this.model.gameEditor.addViewAction({
            type: "normal",
            displayName: "Cancel",
            name: "CancelOsCatalogDetails",
            handler: (this._onOsCatalogDetailsCancel = this._onOsCatalogDetailsCancel.bind(this))
        });

        this._renderingForm = false;
        this._registerListeners();
    }

    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/

    _registerListeners() {
        this._model.on("Dirty", (this._onDirty = this._onDirty.bind(this)));
        this._model.on("OsCatalogPropertiesChanged", (this._onOsCatalogPropertiesChanged = this._onOsCatalogPropertiesChanged.bind(this)));
        this._model.on("ConfirmationModelHidden", (this._onConfirmationModelHidden = this._onConfirmationModelHidden.bind(this)));
        this._model.on("DisableViewActions", (this._onDisableViewActions = this._onDisableViewActions.bind(this)));
        this._formsComponent.on(
            "PendingPatchesChanged",
            (this._onFormsPendingPatchesChanged = _.debounce(this._onFormsPendingPatchesChanged.bind(this), 500, false))
        );
    }

    _unregisterListeners() {
        this._model.removeListener("Dirty", this._onDirty);
        this._model.removeListener("OsCatalogPropertiesChanged", this._onOsCatalogPropertiesChanged);
        this._model.removeListener("ConfirmationModelHidden", this._onConfirmationModelHidden);
        this._model.removeListener("DisableViewActions", this._onDisableViewActions);
        this._formsComponent.removeListener("PendingPatchesChanged", this._onFormsPendingPatchesChanged);
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/

    shutdown() {
        this._unregisterListeners();
        this._onFormsPendingPatchesChanged.flush();
        let promArr = [this._formsComponent.shutdown(), this._skusSection.shutdown(), this._imagesSection.shutdown()];

        return Promise.all(promArr).then(
            () => {
                this._formsComponent = undefined;
                this._renderingForm = undefined;
                this._isDirty = undefined;
                this._logger = undefined;
                this._model = undefined;
                this._rootElem.remove();
                this._rootElem = undefined;
            },
            (err) => {
                this._logger.warn({ error: err }, "OsCatalogDetailsViewRenderer.shutdown() error.");
            }
        );
    }

    reRenderView() {
        return this._renderSkus()
            .then(() => {
                this._imagesSection._reestablishfeed();
                this._renderImages();
                return Promise.resolve();
            })
            .catch((err) => this._logger.warn({ error: err }, "OsCatalogDetailsViewRenderer._onShutdown()"));
    }
    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render() {
        return this.model.whenModelIsReady().then(
            () => {
                this._toggleActions();
                return this._renderOsCatalogDetails();
            },
            (err) => err
        );
    }

    get rootElement() {
        return this.getRootElement();
    }

    getRootElement() {
        return this._rootElem;
    }

    get model() {
        return this.getModel();
    }

    getModel() {
        return this._model;
    }

    /*******************************************/
    /* END COMPONENT RENDERER METHODS          */
    /* START PRIVATE ACTION METHODS            */
    /*******************************************/

    _showViewActions() {
        this.model.gameEditor.showViewAction("SaveOsCatalogDetails");
        this.model.gameEditor.showViewAction("CancelOsCatalogDetails");
    }

    _hideViewActions() {
        this.model.gameEditor.hideViewAction("SaveOsCatalogDetails");
        this.model.gameEditor.hideViewAction("CancelOsCatalogDetails");
    }

    _disableViewActions() {
        this.model.gameEditor.disableViewAction("SaveOsCatalogDetails");
        this.model.gameEditor.disableViewAction("CancelOsCatalogDetails");
    }

    _enableViewActions() {
        this.model.gameEditor.enableViewAction("SaveOsCatalogDetails");
        this.model.gameEditor.enableViewAction("CancelOsCatalogDetails");
    }

    _toggleActions() {
        if (this._isDirty) {
            this._enableViewActions();
        } else {
            this._disableViewActions();
        }
    }

    /*******************************************/
    /* END PRIVATE ACTION METHODS              */
    /* START PRIVATE RENDER METHODS            */
    /*******************************************/

    _renderOsCatalogDetails() {
        return Promise.resolve().then(
            () => {
                let jtype = this._getJtype();
                let pristineValue = this._getPristineValues();
                this._formsComponent.setTypeInformation(jtype, true);
                this._formsComponent.setPristineValue(pristineValue, true);
                this._renderingForm = true;
                this._rootElem.appendChild(this._formsComponent.renderer.rootElement);
                return this._formsComponent.renderer
                    .render()
                    .then(() => {
                        this._renderingForm = false;
                    })
                    .then(() => {
                        return this._renderSkus();
                    })
                    .then(() => {
                        return this._renderImages();
                    });
            },
            (err) => {
                this._logger.warn({ error: err }, "OSCatalogDetailsViewRenderer._renderOsCatalogDetails() error.");
            }
        );
    }

    _renderSkus() {
        if (_.isUndefined(this._skusSection)) {
            this._skusSection = new OsCatalogSkusComponent(this._model);
            this._rootElem.appendChild(this._skusSection.renderer.rootElement);
            return this._skusSection.renderer.render();
        } else {
            this._skusSection._renderer.rootElement.innerHTML = "";
            this._rootElem.appendChild(this._skusSection.renderer.rootElement);
            return this._skusSection.renderer.render();
            /* this._skusSection.updateSkus();
            return Promise.resolve(); */
        }
    }

    _renderImages() {
        if (_.isUndefined(this._imagesSection)) {
            this._imagesSection = new OsCatalogImagesComponent(this._model);
            return this._imagesSection.renderer.render().then(() => {
                this._rootElem.appendChild(this._imagesSection.renderer.rootElement);
                //this._imagesSection.renderer._treeComponent.renderer.render(); // rendering the Tree View after the parent DOM is appended in the document.
                this._model.emit("RenderTree");
            });
        } else {
            this._imagesSection._renderer.rootElement.innerHTML = "";
            this._rootElem.appendChild(this._imagesSection._renderer.rootElement);
            return this._imagesSection.renderer.render().then(() => {
                this._rootElem.appendChild(this._imagesSection.renderer.rootElement);
                // this._imagesSection.renderer._treeComponent.renderer.render(); // rendering the Tree View after the parent DOM is appended in the document.
                this._model.emit("RenderTree");
            });
        }
    }

    /*******************************************/
    /* END PRIVATE RENDER METHODS              */
    /* START HANDLER METHODS                   */
    /*******************************************/

    _onFormsPendingPatchesChanged() {
        if (!this._renderingForm) {
            const form = this._formsComponent.getValue(),
                formName = form.name,
                formDescription = form.description;

            if (!_.isEqual(this._model.osCatalog.getName(), formName)) {
                this._model.osCatalog.setName(formName);
            }

            if (formDescription.length === formDescription.trim().length && !_.isEqual(this._model.osCatalog.getDescription(), formDescription)) {
                this._model.osCatalog.setDescription(form.description);
            }
        }

        return Promise.resolve();
    }

    _onConfirmationModelHidden() {
        this._skusSection
            .trimSkusRegexJunkValues()
            .then(() => {
                if (this._model.isCatalogFormValid(true)) {
                    return this._model.saveCatalog();
                }
            })
            .then(() => {
                this._model._gameEditor.closeEditor(this._model._gameEditor.editorTabStateId, true);
            })
            .catch((error) => {
                // Not catching the error throws console log
                this._logger.info({ error }, "Cannot save as the data is invalid");
            });
    }

    _onDisableViewActions() {
        this._disableViewActions();
    }

    _onOsCatalogPropertiesChanged() {
        if (!this._renderingForm) {
            let osCatalogPropsVal = this.model.osCatalog.getPristineProperties();
            let isDirty = this._isDirty;
            if (isDirty) {
                this._model.gameEditor.runQuickConfirmationActionWorkflow(
                    {
                        message: "This Catalog has been updated by another user. Do you want to refresh its details? All changes will be lost.",
                        title: "Update Confirmation Check",
                        confirmDetails: {
                            text: "Yes",
                            type: "text"
                        }
                    },
                    (this._onOsCatalogDetailsCancelAction = this._onOsCatalogDetailsCancelAction.bind(this))
                );
            } else {
                this._formsComponent.setPristineValue(osCatalogPropsVal);
                this._imagesSection.renderer._updateSelSelectbox();
                this._skusSection.updateSkus();

                //this.reRenderView();
            }
        }
    }
    _onRefreshView() {
        console.log("TODO Live Update image Section only when Image update");
    }
    _onDirty(dirtyState) {
        this._isDirty = dirtyState;
        this._toggleActions();
    }

    _onOsCatalogDetailsSave() {
        this._skusSection
            .trimSkusRegexJunkValues()
            .then(() => {
                return this._model.saveCatalog();
            })
            .catch(() => {
                this._enableViewActions();
            });
    }

    _onOsCatalogDetailsCancel() {
        this.model.gameEditor.runQuickConfirmationActionWorkflow(
            {
                message: "Are you sure you want to cancel? All changes will be lost.",
                title: "Cancel Confirmation Check",
                confirmDetails: {
                    text: "Yes",
                    type: "text"
                }
            },
            (this._onOsCatalogDetailsCancelAction = this._onOsCatalogDetailsCancelAction.bind(this))
        );
    }

    _onOsCatalogDetailsCancelAction() {
        let osCatalogPropsVal = this.model.osCatalog.getPristineProperties();
        this._formsComponent.setPristineValue(osCatalogPropsVal);
        this._model.resetCatalogProperties();
        this.reRenderView().then(() => {
            this.model._gameEditor._tabCmpnt.setDirty(false);
        });
        return Promise.resolve();
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE API METHODS               */
    /*******************************************/
    _getPristineValues() {
        const pristineProperties = this.model.osCatalog.getPristineProperties();
        let pristineValues = _.cloneDeep(pristineProperties);
        return pristineValues;
    }

    _getJtype() {
        let jtype = {};
        jtype.name = "";
        jtype["name@Display.label"] = "Name";

        jtype.description = "";
        jtype["description@Display.label"] = "Description";

        jtype.lineBreak = null;
        jtype["lineBreak@Display.label"] = "&nbsp;";

        if (this._model._gameEditor.userAccessToPlan) {
            jtype["name@Display.readOnly"] = true;
            jtype["description@Display.readOnly"] = true;
        } else {
            jtype["name@Display.readOnly"] = false;
            jtype["description@Display.readOnly"] = false;
        }

        return jtype;
    }

    /*******************************************/
    /*  END PRIVATE API METHODS                */
    /*******************************************/
}

module.exports = OsCatalogDetailsViewRenderer;
